import { IconButton, SxProps, ToggleButton } from '@mui/material';
import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import StarIcon from '@mui/icons-material/Star';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import {
    selectAddFavoriteError,
    selectAddFavoriteStatus,
    selectDeleteFavoriteError,
    selectDeleteFavoriteStatus,
    selectFavorites,
} from '@features/favorites/store/selectors';
import {
    addFavoriteRequest,
    addFavoriteReset,
    deleteFavoriteRequest,
    deleteFavoriteReset,
} from '@features/favorites/store';
import { FavoriteObjectEntity, FavoriteObjectId } from '@features/favorites/types';
import { MAP_ENTITY_TO_PARAM_NAME } from '@features/favorites/api/favoritesApi';
import debounce from 'lodash/debounce';

interface FavoriteButtonProps {
    objectEntity: FavoriteObjectEntity;
    objectId: FavoriteObjectId;
    buttonType?: 'icon' | 'toggle';
    sx?: SxProps;
}

const FavoriteButton: FC<FavoriteButtonProps> = ({ objectEntity, objectId, buttonType = 'icon', sx }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [favorite, setFavorite] = useState(false); // optimistic UI value
    const favoritesIdsArray = useSelector(selectFavorites(objectEntity));
    const addFavoriteStatus = useSelector(selectAddFavoriteStatus(objectEntity));
    const deleteFavoriteStatus = useSelector(selectDeleteFavoriteStatus(objectEntity));
    const addFavoriteError = useSelector(selectAddFavoriteError(objectEntity));
    const deleteFavoriteError = useSelector(selectDeleteFavoriteError(objectEntity));
    const favoriteStoredValue = Boolean(
        favoritesIdsArray.find((favoriteId: FavoriteObjectId) => favoriteId === objectId),
    ); // real stored on backend value
    const favoriteRef = useRef(favorite); // we need to use useRef because without it lodash will take old value
    const favoriteStoredRef = useRef(favoriteStoredValue);

    useEffect(() => {
        setFavorite(favoriteStoredValue);
    }, [favoriteStoredValue]);

    // Update ref to store actual value
    useEffect(() => {
        favoriteRef.current = favorite;
    }, [favorite]);

    useEffect(() => {
        favoriteStoredRef.current = favoriteStoredValue;
    }, [favoriteStoredValue]);

    const debouncedSync = useMemo(
        () =>
            debounce(() => {
                const current = favoriteRef.current;
                const stored = favoriteStoredRef.current;

                if (current === stored) {
                    // Nothing to sync - optimistic ui value is up-to-date
                    return;
                }

                if (current) {
                    dispatch(addFavoriteRequest({ entity: objectEntity, id: objectId }));
                } else {
                    dispatch(deleteFavoriteRequest({ entity: objectEntity, id: objectId }));
                }
            }, 500),
        [dispatch, objectEntity, objectId],
    );

    // Clear debounce function on unmount
    useEffect(() => {
        return () => {
            debouncedSync.cancel();
        };
    }, [debouncedSync]);

    const toggleFavoriteObject = (e: SyntheticEvent) => {
        e.stopPropagation();

        setFavorite((prev) => {
            const next = !prev;
            favoriteRef.current = next;
            debouncedSync();
            return next;
        });
    };

    const showAddError = useMemo(
        () =>
            (addFavoriteStatus === REQUEST_STATUS.ERROR &&
                addFavoriteError &&
                addFavoriteError.config &&
                JSON.parse(addFavoriteError.config.data)[MAP_ENTITY_TO_PARAM_NAME[objectEntity]]) === objectId,
        [addFavoriteError, addFavoriteStatus, objectId, objectEntity],
    );

    const showDeleteError = useMemo(
        () =>
            deleteFavoriteStatus === REQUEST_STATUS.ERROR &&
            deleteFavoriteError &&
            deleteFavoriteError.config &&
            JSON.parse(deleteFavoriteError.config.data)[MAP_ENTITY_TO_PARAM_NAME[objectEntity]] === objectId,
        [deleteFavoriteError, deleteFavoriteStatus, objectId, objectEntity],
    );

    useEffect(() => {
        if (showAddError || showDeleteError) {
            setFavorite((prev) => !prev);
            return;
        }
    }, [showAddError, showDeleteError]);

    return (
        <>
            {buttonType === 'icon' && (
                <IconButton onClick={toggleFavoriteObject}>
                    {favorite ? <StarIcon color="primary" /> : <StarBorderOutlinedIcon />}
                </IconButton>
            )}
            {buttonType === 'toggle' && (
                <ToggleButton
                    sx={{ border: 0, ...sx }}
                    value={favorite}
                    selected={favorite}
                    onClick={toggleFavoriteObject}
                >
                    {favorite ? <StarIcon color="primary" /> : <StarBorderOutlinedIcon />}
                </ToggleButton>
            )}
            {showAddError && (
                <ModalErrorContainer
                    object={t('favorite_r') + ' ' + t('object_r').toLowerCase()}
                    action={t('adding')}
                    errorSelector={selectAddFavoriteError(objectEntity)}
                    resetActionCallback={addFavoriteReset}
                    resetActionCallbackParams={{ entity: objectEntity }}
                />
            )}
            {showDeleteError && (
                <ModalErrorContainer
                    object={t('favorite_r') + ' ' + t('object_r').toLowerCase()}
                    action={t('deleting')}
                    errorSelector={selectDeleteFavoriteError(objectEntity)}
                    resetActionCallback={deleteFavoriteReset}
                    resetActionCallbackParams={{ entity: objectEntity }}
                />
            )}
        </>
    );
};

export default FavoriteButton;
